import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, Loader2, AlertCircle, Lock } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useGetSectors } from "@/hooks/api/query/useGetSectors";
import type { Sector } from "@/types/risk-analysis";

interface SectorSelectionListProps {
  selectedSectors: string[];
  onToggle: (sectorId: string) => void;
  showSearch?: boolean;
  inModal?: boolean;
  disabledSectors?: string[]; // Array of sector IDs that should be disabled
}

export function SectorSelectionList({
  selectedSectors,
  onToggle,
  showSearch = true,
  inModal = false,
  disabledSectors = [],
}: SectorSelectionListProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"1" | "2">("1");
  const {
    data: sectorsData,
    isLoading,
    error,
  } = useGetSectors({
    context: "risk-analysis",
  });

  const annexISectors = sectorsData?.filter((s) => s.annex === "I") ?? [];
  const annexIISectors = sectorsData?.filter((s) => s.annex === "II") ?? [];

  const filterSectors = (sectors: Sector[]) => {
    if (!searchQuery.trim()) return sectors;
    const query = searchQuery.toLowerCase();
    return sectors.filter(
      (s) =>
        s.nume.toLowerCase().includes(query) ||
        s.cod.toLowerCase().includes(query) ||
        s.subsectoare.toLowerCase().includes(query)
    );
  };

  const filteredAnnexI = filterSectors(annexISectors);
  const filteredAnnexII = filterSectors(annexIISectors);

  // Count selected sectors in each annex
  const selectedInAnnexI = annexISectors.filter((s) =>
    selectedSectors.includes(s.id)
  ).length;
  const selectedInAnnexII = annexIISectors.filter((s) =>
    selectedSectors.includes(s.id)
  ).length;

  // Auto-select tab based on where selections exist
  useEffect(() => {
    if (activeTab === "1" && selectedInAnnexI === 0 && selectedInAnnexII > 0) {
      setActiveTab("2");
    }
  }, [selectedSectors, selectedInAnnexI, selectedInAnnexII]);

  const handleSelectAll = (sectors: Sector[]) => {
    sectors.forEach((sector) => {
      if (
        !selectedSectors.includes(sector.id) &&
        !disabledSectors.includes(sector.id)
      ) {
        onToggle(sector.id);
      }
    });
  };

  const handleDeselectAll = (sectors: Sector[]) => {
    sectors.forEach((sector) => {
      if (
        selectedSectors.includes(sector.id) &&
        !disabledSectors.includes(sector.id)
      ) {
        onToggle(sector.id);
      }
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center py-12 text-destructive">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>Eroare la încărcarea sectoarelor: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 md:space-y-4">
      <div className="flex flex-wrap items-center justify-between md:gap-4 gap-2">
        <Label className="md:text-base text-sm font-semibold hidden md:block">
          Selectați sectoarele pentru analiză (minim 1)
        </Label>
        <Badge variant="secondary" className="md:text-sm text-xs">
          {selectedSectors.length} selectate
        </Badge>
      </div>

      {showSearch && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 md:w-4 md:h-4 w-3 h-3 text-muted-foreground" />
          <Input
            placeholder="Căutare sector..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="md:pl-9 pl-7"
          />
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as "1" | "2")}
      >
        <div className="flex md:items-center flex-col-reverse items-start md:flex-row flex-wrap md:justify-between mb-2 md:mb-4">
          <TabsList className="bg-gray-100 p-1">
            <TabsTrigger
              value="1"
              className="text-xs md:text-sm flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:hover:bg-gray-200 transition-all duration-200"
            >
              Anexa 1
              <Badge variant={activeTab === "1" ? "secondary" : "outline"}>
                {selectedInAnnexI}/{annexISectors.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger
              value="2"
              className="text-xs md:text-sm flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:hover:bg-gray-200 transition-all duration-200"
            >
              Anexa 2
              <Badge variant={activeTab === "2" ? "secondary" : "outline"}>
                {selectedInAnnexII}/{annexIISectors.length}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <div className="flex gap-2 md:self-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                handleSelectAll(
                  activeTab === "1" ? filteredAnnexI : filteredAnnexII
                )
              }
            >
              Selectează toate
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                handleDeselectAll(
                  activeTab === "1" ? filteredAnnexI : filteredAnnexII
                )
              }
            >
              Deselectează toate
            </Button>
          </div>
        </div>

        <TabsContent value="1" className="mt-0">
          <div
            className={`grid gap-2 ${inModal ? "max-h-[250px] md:max-h-[400px] overflow-y-auto" : ""}`}
          >
            {filteredAnnexI.map((sector) => {
              const isDisabled = disabledSectors.includes(sector.id);
              const labelContent = (
                <label
                  className={`flex items-start space-x-3 p-2 md:p-3 border rounded-lg transition-colors min-h-[48px] md:min-h-[60px] ${
                    isDisabled
                      ? "opacity-60 bg-muted cursor-not-allowed"
                      : "cursor-pointer hover:bg-muted/50"
                  }`}
                >
                  <Checkbox
                    id={sector.id}
                    checked={selectedSectors.includes(sector.id)}
                    onCheckedChange={() => onToggle(sector.id)}
                    className="mt-1"
                    disabled={isDisabled}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium">{sector.nume}</div>
                      {isDisabled && (
                        <Badge variant="secondary" className="text-xs">
                          <Lock className="h-3 w-3 mr-1" />
                          Selectat
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {sector.subsectoare}
                    </p>
                  </div>
                </label>
              );

              return isDisabled ? (
                <TooltipProvider key={sector.id}>
                  <Tooltip>
                    <TooltipTrigger asChild>{labelContent}</TooltipTrigger>
                    <TooltipContent>
                      <p>Sectorul nu poate fi deselectat</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <div key={sector.id}>{labelContent}</div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="2" className="mt-0">
          <div
            className={`grid gap-2 ${inModal ? "max-h-[400px] overflow-y-auto" : ""}`}
          >
            {filteredAnnexII.map((sector) => {
              const isDisabled = disabledSectors.includes(sector.id);
              const labelContent = (
                <label
                  className={`flex items-start space-x-3 p-2 md:p-3 border rounded-lg transition-colors min-h-[48px] md:min-h-[60px] ${
                    isDisabled
                      ? "opacity-60 bg-muted cursor-not-allowed"
                      : "cursor-pointer hover:bg-muted/50"
                  }`}
                >
                  <Checkbox
                    id={sector.id}
                    checked={selectedSectors.includes(sector.id)}
                    onCheckedChange={() => onToggle(sector.id)}
                    className="mt-1"
                    disabled={isDisabled}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium">{sector.nume}</div>
                      {isDisabled && (
                        <Badge variant="secondary" className="text-xs">
                          <Lock className="h-3 w-3 mr-1" />
                          Selectat
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {sector.subsectoare}
                    </p>
                  </div>
                </label>
              );

              return isDisabled ? (
                <TooltipProvider key={sector.id}>
                  <Tooltip>
                    <TooltipTrigger asChild>{labelContent}</TooltipTrigger>
                    <TooltipContent>
                      <p>Sectorul nu poate fi deselectat</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <div key={sector.id}>{labelContent}</div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
